<template>
  <section class="waajo-ticket-attachment-panel">
    <div class="waajo-ticket-attachment-head">
      <div class="waajo-ticket-attachment-head-copy">
        <h4><i class="fa fa-picture-o"></i> Lampiran Gambar</h4>
        <p>Bukti gambar atau screenshot pendukung tiket ini.</p>
      </div>
      <div class="waajo-ticket-attachment-head-side">
        <span class="waajo-ticket-attachment-count">
          {{ items.length }}/{{ maxFiles }} gambar
        </span>
        <button
          type="button"
          class="waajo-ticket-attachment-refresh"
          title="Muat ulang lampiran"
          :disabled="loading || uploading"
          @click="refreshAttachments"
        >
          <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
        </button>
      </div>
    </div>

    <div v-if="loading && !items.length" class="waajo-ticket-attachment-loading">
      <span class="waajo-ticket-attachment-sk"></span>
      <span class="waajo-ticket-attachment-sk is-short"></span>
    </div>

    <div v-else-if="loadError && !items.length" class="waajo-ticket-attachment-state is-error">
      <i class="fa fa-exclamation-circle"></i>
      <span>{{ loadError }}</span>
      <button type="button" @click="refreshAttachments">Coba lagi</button>
    </div>

    <template v-else>
      <div v-if="loadError" class="waajo-ticket-attachment-notice is-error">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ loadError }}</span>
        <button type="button" @click="refreshAttachments">Coba lagi</button>
      </div>

      <div v-if="expiredNotice" class="waajo-ticket-attachment-notice is-warning">
        <i class="fa fa-clock-o"></i>
        <span>
          Sebagian pratinjau tidak dapat dimuat karena tautan gambar sudah
          kedaluwarsa.
        </span>
        <button type="button" :disabled="loading" @click="refreshAttachments">
          Muat ulang gambar
        </button>
      </div>

      <ticket-attachment-grid
        v-if="displayItems.length"
        :items="displayItems"
        :removable="!readonly"
        downloadable
        :disabled="readonly || uploading"
        :busy-id="deletingId"
        :downloading-id="downloadingId"
        @preview="openPreview"
        @download="downloadItem"
        @remove="confirmDelete"
        @image-error="markBroken"
      />

      <div v-else class="waajo-ticket-attachment-empty">
        <i class="fa fa-picture-o"></i>
        <p>Belum ada gambar lampiran.</p>
        <small v-if="!readonly">Unggah bukti gambar atau screenshot pendukung tiket.</small>
      </div>

      <div v-if="validationMessages.length" class="waajo-ticket-attachment-error">
        <i class="fa fa-exclamation-circle"></i>
        <div>
          <p v-for="(message, index) in validationMessages" :key="'validation-' + index">
            {{ message }}
          </p>
        </div>
      </div>

      <div v-if="uploadError" class="waajo-ticket-attachment-error">
        <i class="fa fa-exclamation-triangle"></i>
        <div>
          <p>{{ uploadError }}</p>
          <button
            v-if="pendingFiles.length"
            type="button"
            :disabled="uploading"
            @click="retryUpload"
          >
            <i class="fa fa-refresh"></i> Coba unggah lagi
          </button>
        </div>
      </div>

      <div v-if="actionError" class="waajo-ticket-attachment-error">
        <i class="fa fa-exclamation-circle"></i>
        <div>
          <p>{{ actionError }}</p>
        </div>
      </div>

      <ticket-attachment-dropzone
        v-if="!readonly"
        :remaining="remaining"
        :max-files="maxFiles"
        :busy="uploading"
        :progress="uploadProgress"
        :disabled="loading"
        @files="handleFiles"
        @rejected="handleRejected"
      />

      <small v-if="!readonly" class="waajo-ticket-attachment-footnote">
        Gambar dikonversi otomatis ke WebP oleh server. GIF animasi disimpan
        sebagai frame pertama.
      </small>
    </template>

    <ticket-attachment-preview
      :visible="previewVisible"
      :items="displayItems"
      :start-index="previewIndex"
      @close="closePreview"
      @download="downloadItem"
      @refresh="refreshAttachments"
      @image-error="markBroken"
    />
  </section>
</template>

<script>
import { extractApiErrorMessage } from '@/utils/ticket'
import {
  TICKET_ATTACHMENT_MAX_FILES,
  ticketAttachmentCapacityReason,
  ticketAttachmentDownloadName
} from '@/utils/ticketAttachment'
import TicketAttachmentDropzone from './TicketAttachmentDropzone.vue'
import TicketAttachmentGrid from './TicketAttachmentGrid.vue'
import TicketAttachmentPreview from './TicketAttachmentPreview.vue'

const MAX_VALIDATION_MESSAGES = 6

function readBlobText(blob) {
  if (!blob) return Promise.resolve('')
  if (typeof blob.text === 'function') return blob.text()
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => resolve('')
    reader.readAsText(blob)
  })
}

export default {
  name: 'TicketAttachments',
  components: {
    TicketAttachmentDropzone,
    TicketAttachmentGrid,
    TicketAttachmentPreview
  },
  props: {
    ticketId: {
      type: [String, Number],
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxFiles: TICKET_ATTACHMENT_MAX_FILES,
      items: [],
      loading: false,
      loadError: '',
      listRequestId: 0,
      uploading: false,
      uploadProgress: 0,
      uploadError: '',
      pendingFiles: [],
      validationMessages: [],
      actionError: '',
      deletingId: null,
      downloadingId: null,
      brokenIds: [],
      previewVisible: false,
      previewIndex: 0,
      isDestroyed: false
    }
  },
  computed: {
    remaining() {
      return Math.max(0, this.maxFiles - this.items.length)
    },
    displayItems() {
      return (this.items || []).map(item => ({
        key: 'attachment-' + item.id,
        id: item.id,
        url: item.download_url || '',
        name: item.file_name || 'Lampiran',
        size: item.file_size,
        width: item.width,
        height: item.height,
        broken: this.brokenIds.indexOf(item.id) !== -1
      }))
    },
    expiredNotice() {
      return this.brokenIds.length > 0
    }
  },
  watch: {
    ticketId: {
      immediate: true,
      handler() {
        this.resetState()
        if (this.ticketId) this.fetchAttachments()
      }
    },
    'displayItems.length'(value) {
      if (!this.previewVisible) return
      if (!value) {
        this.closePreview()
        return
      }
      if (this.previewIndex > value - 1) this.previewIndex = value - 1
    }
  },
  beforeDestroy() {
    // Naikkan request id agar response yang masih berjalan tidak menulis state.
    this.isDestroyed = true
    this.listRequestId += 1
  },
  methods: {
    resetState() {
      this.listRequestId += 1
      this.items = []
      this.loading = false
      this.loadError = ''
      this.uploading = false
      this.uploadProgress = 0
      this.uploadError = ''
      this.pendingFiles = []
      this.validationMessages = []
      this.actionError = ''
      this.deletingId = null
      this.downloadingId = null
      this.brokenIds = []
      this.previewVisible = false
      this.previewIndex = 0
    },
    isSameTicket(ticketId) {
      if (this.isDestroyed) return false
      if (!this.ticketId || !ticketId) return false
      return String(this.ticketId) === String(ticketId)
    },
    async fetchAttachments(options) {
      const settings = options || {}
      if (!this.ticketId) {
        this.items = []
        this.loading = false
        return
      }

      const ticketId = String(this.ticketId)
      const requestId = this.listRequestId + 1
      this.listRequestId = requestId
      if (!settings.silent) this.loading = true
      this.loadError = ''

      try {
        const response = await this.$store.dispatch('ticket/attachments', this.ticketId)
        if (requestId !== this.listRequestId || !this.isSameTicket(ticketId)) return
        this.items = Array.isArray(response && response.data) ? response.data : []
        this.brokenIds = []
        this.$emit('attachments-updated', {
          ticketId: this.ticketId,
          items: this.items.slice()
        })
      } catch (error) {
        if (requestId !== this.listRequestId || !this.isSameTicket(ticketId)) return
        this.loadError = extractApiErrorMessage(error, 'Gagal memuat lampiran gambar.')
      } finally {
        if (requestId === this.listRequestId && this.isSameTicket(ticketId)) {
          this.loading = false
        }
      }
    },
    refreshAttachments() {
      this.actionError = ''
      this.fetchAttachments({ silent: true })
    },
    handleFiles(files) {
      if (this.readonly) return
      this.validationMessages = []
      this.actionError = ''

      const list = Array.from(files || []).filter(Boolean)
      if (!list.length) return

      if (this.uploading) {
        this.validationMessages = ['Unggahan sedang berjalan. Tunggu hingga selesai.']
        return
      }

      const available = this.remaining
      if (available <= 0) {
        this.validationMessages = [ticketAttachmentCapacityReason({ maxFiles: this.maxFiles })]
        return
      }

      const accepted = list.slice(0, available)
      if (list.length > accepted.length) {
        this.validationMessages = [
          list.length -
            accepted.length +
            ' gambar tidak diunggah karena ' +
            ticketAttachmentCapacityReason({ maxFiles: this.maxFiles }).toLowerCase()
        ]
      }

      this.performUpload(accepted)
    },
    handleRejected(rejected) {
      const list = Array.isArray(rejected) ? rejected : []
      this.validationMessages = list
        .slice(0, MAX_VALIDATION_MESSAGES)
        .map(item => {
          if (!item) return 'Gambar tidak dapat diunggah.'
          const name = String(item.name || '').trim()
          const reason = String(item.reason || 'Gambar tidak dapat diunggah.')
          return name ? name + ': ' + reason : reason
        })
    },
    async performUpload(files) {
      const list = Array.from(files || []).filter(Boolean)
      if (!list.length || this.uploading) return

      const ticketId = String(this.ticketId)
      this.uploading = true
      this.uploadProgress = 0
      this.uploadError = ''
      this.pendingFiles = []

      try {
        const response = await this.$store.dispatch('ticket/uploadAttachments', {
          ticketId: this.ticketId,
          files: list,
          onProgress: percent => {
            if (this.isSameTicket(ticketId) && this.uploading) {
              this.uploadProgress = percent
            }
          }
        })

        if (!this.isSameTicket(ticketId)) return

        const uploaded = response && Array.isArray(response.data)
          ? response.data.length
          : list.length
        this.uploadProgress = 100
        await this.fetchAttachments({ silent: true })
        if (!this.isSameTicket(ticketId)) return
        this.$emit('uploaded', { ticketId: this.ticketId, count: uploaded })
      } catch (error) {
        if (!this.isSameTicket(ticketId)) return
        this.uploadError = extractApiErrorMessage(error, 'Gagal mengunggah gambar.')
        this.pendingFiles = list.slice()
        this.$emit('upload-error', this.uploadError)

        const message = String(this.uploadError || '').toLowerCase()
        if (
          message.indexOf('maksimal') !== -1 ||
          message.indexOf('kapasitas') !== -1 ||
          message.indexOf('tidak ditemukan') !== -1
        ) {
          this.fetchAttachments({ silent: true })
        }
        if (message.indexOf('tidak ditemukan') !== -1) {
          this.$emit('ticket-missing', this.ticketId)
        }
      } finally {
        if (this.isSameTicket(ticketId)) this.uploading = false
      }
    },
    retryUpload() {
      if (!this.pendingFiles.length || this.uploading) return
      const files = this.pendingFiles.slice()
      this.validationMessages = []
      this.performUpload(files)
    },
    openPreview(index) {
      this.previewIndex = Math.max(0, Number(index) || 0)
      this.previewVisible = true
    },
    closePreview() {
      this.previewVisible = false
    },
    markBroken(item) {
      if (!item || item.id === undefined || item.id === null) return
      if (this.brokenIds.indexOf(item.id) === -1) {
        this.brokenIds = this.brokenIds.concat([item.id])
      }
    },
    async downloadItem(item) {
      if (!item || item.id === undefined || item.id === null) return
      if (this.downloadingId) return

      this.actionError = ''
      this.downloadingId = item.id
      try {
        const response = await this.$store.dispatch('ticket/downloadAttachment', item.id)
        const payload = response && typeof response === 'object' && 'data' in response
          ? response.data
          : response
        const blob = payload instanceof Blob
          ? payload
          : new Blob([payload], { type: 'image/webp' })

        if (blob.type && blob.type.indexOf('application/json') !== -1) {
          const text = await readBlobText(blob)
          let message = ''
          try {
            const parsed = JSON.parse(text)
            message = parsed && parsed.msg ? parsed.msg : ''
          } catch (parseError) {
            message = ''
          }
          throw new Error(message || 'Gambar gagal diunduh. Coba lagi.')
        }

        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', ticketAttachmentDownloadName(item.name))
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500)
      } catch (error) {
        this.actionError = extractApiErrorMessage(
          error,
          'Gambar gagal diunduh. Periksa koneksi lalu coba lagi.'
        )
        if (String(this.actionError).toLowerCase().indexOf('tidak ditemukan') !== -1) {
          this.fetchAttachments({ silent: true })
        }
      } finally {
        this.downloadingId = null
      }
    },
    swalAboveModal(options) {
      const base = options || {}
      return this.$fire(
        Object.assign({}, base, {
          customClass: Object.assign({}, base.customClass || {}, {
            container: 'waajo-swal-above-ticket-modal'
          }),
          customContainerClass: 'waajo-swal-above-ticket-modal'
        })
      )
    },
    async confirmDelete(item) {
      if (!item || item.id === undefined || item.id === null) return
      if (this.deletingId || this.readonly) return

      const name = item.name || 'gambar ini'
      const confirmation = await this.swalAboveModal({
        title: 'Hapus gambar?',
        text: 'Gambar "' + name + '" akan dihapus dari tiket ini.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, hapus',
        cancelButtonText: 'Batal',
        confirmButtonColor: '#f39c12'
      })
      if (!confirmation || confirmation.value !== true) return

      const ticketId = String(this.ticketId)
      this.deletingId = item.id
      this.actionError = ''

      try {
        await this.$store.dispatch('ticket/deleteAttachment', item.id)
        if (!this.isSameTicket(ticketId)) return
        if (this.previewVisible) this.closePreview()
        await this.fetchAttachments({ silent: true })
        this.$emit('deleted', { ticketId: this.ticketId, id: item.id })
      } catch (error) {
        if (!this.isSameTicket(ticketId)) return
        const message = extractApiErrorMessage(error, 'Gagal menghapus gambar.')
        this.actionError = message
        if (String(message).toLowerCase().indexOf('tidak ditemukan') !== -1) {
          this.fetchAttachments({ silent: true })
        }
      } finally {
        if (this.isSameTicket(ticketId)) this.deletingId = null
      }
    }
  }
}
</script>

<style scoped>
.waajo-ticket-attachment-panel {
  padding: 18px;
  border: 1px solid var(--waajo-border);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}
.waajo-ticket-attachment-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.waajo-ticket-attachment-head-copy {
  min-width: 0;
}
.waajo-ticket-attachment-head-copy h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-attachment-head-copy h4 i {
  color: var(--waajo-brand);
  margin-right: 7px;
}
.waajo-ticket-attachment-head-copy p {
  margin: 5px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
}
.waajo-ticket-attachment-head-side {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.waajo-ticket-attachment-count {
  padding: 3px 9px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9999px;
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.waajo-ticket-attachment-refresh {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: var(--waajo-text-muted);
  font-size: 12px;
}
.waajo-ticket-attachment-refresh:hover,
.waajo-ticket-attachment-refresh:focus {
  border-color: var(--waajo-brand-border-strong);
  color: var(--waajo-brand-ink);
}
.waajo-ticket-attachment-refresh:disabled {
  opacity: 0.6;
}
.waajo-ticket-attachment-loading {
  padding: 6px 0;
}
.waajo-ticket-attachment-sk {
  display: block;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  border-radius: 5px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: waajoTicketAttachmentSk 1.2s infinite;
}
.waajo-ticket-attachment-sk.is-short {
  width: 62%;
}
@keyframes waajoTicketAttachmentSk {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.waajo-ticket-attachment-state {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 12px 0;
  color: var(--waajo-text-muted);
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-attachment-state.is-error {
  color: #b42318;
}
.waajo-ticket-attachment-state button,
.waajo-ticket-attachment-notice button {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
  padding: 0;
}
.waajo-ticket-attachment-notice {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 9px 11px;
  margin-bottom: 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-attachment-notice.is-warning {
  border: 1px solid var(--waajo-brand-border);
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink-strong);
}
.waajo-ticket-attachment-notice.is-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}
.waajo-ticket-attachment-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 18px 12px;
  margin-bottom: 12px;
  border: 1px dashed var(--waajo-border);
  border-radius: 12px;
  background: var(--waajo-surface-soft);
  text-align: center;
  color: var(--waajo-text-muted);
  font-size: 13px;
}
.waajo-ticket-attachment-empty i {
  font-size: 24px;
  color: #cbd5e1;
}
.waajo-ticket-attachment-empty p {
  margin: 0;
  font-weight: 600;
  color: var(--waajo-text-body);
}
.waajo-ticket-attachment-empty small {
  font-size: 12px;
  color: var(--waajo-text-faint);
}
.waajo-ticket-attachment-error {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-attachment-error p {
  margin: 0 0 3px;
}
.waajo-ticket-attachment-error p:last-child {
  margin-bottom: 0;
}
.waajo-ticket-attachment-error button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  min-height: 30px;
  padding: 0 12px;
  border: 1px solid #fca5a5;
  border-radius: 9999px;
  background: #fff;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 700;
}
.waajo-ticket-attachment-error button:disabled {
  opacity: 0.6;
}
.waajo-ticket-attachment-footnote {
  display: block;
  margin-top: 8px;
  font-size: 11px;
  line-height: 1.5;
  color: var(--waajo-text-faint);
}
@media (max-width: 640px) {
  .waajo-ticket-attachment-panel {
    padding: 14px;
  }
  .waajo-ticket-attachment-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketAttachments.vue